import React, { useState, useRef, useEffect } from 'react';
import { Text } from '@ui/atoms';
import TabHeader from './TabHeader';
import TabContent from './TabContent';
import { tabsContent } from './constants';
import { useRouter } from 'next/router';
import { ArrowRightIcon } from '@heroicons/react/outline';
import { getAuth } from '@services/identity.service';
import useClient from '@hooks/useClient';
import { triggerCustomGaEvent } from '@helpers/miscelleanous';
const CategoriesTabs = ({ loginModal }) => {
    const [selectedTab, setSelectedTab] = useState(1);
    const sectionRefs = useRef(tabsContent.map(() => React.createRef()));
    const isScrollingToTab = useRef(false);
    const router = useRouter();
    const auth = getAuth();
    const { isClient } = useClient();

    // Scroll to section on tab click
    const handleTabClick = (idx, tabId) => {
        setSelectedTab(tabId);
        isScrollingToTab.current = true;
        sectionRefs.current[idx].current.scrollIntoView({
            behavior: 'smooth',
            block: 'start',
        });
        // Prevent scroll handler from firing during programmatic scroll
        setTimeout(() => {
            isScrollingToTab.current = false;
        }, 800);
    };

    // On scroll, update selected tab
    useEffect(() => {
        const handleScroll = () => {
            if (isScrollingToTab.current) return;
            const offsets = sectionRefs.current.map((ref) => {
                if (!ref.current) return Number.POSITIVE_INFINITY;
                const rect = ref.current.getBoundingClientRect();
                return Math.abs(rect.top - 120); // 120px offset for header
            });
            const minIdx = offsets.indexOf(Math.min(...offsets));
            if (tabsContent[minIdx] && tabsContent[minIdx].id !== selectedTab) {
                setSelectedTab(tabsContent[minIdx].id);
            }
        };
        window.addEventListener('scroll', handleScroll, { passive: true });
        return () => window.removeEventListener('scroll', handleScroll);
    }, [selectedTab]);

    return (
        <div className="xl:rounded-t-[40px] bg-homepage-why-bonds-bg pt-12 pb-20 w-full relative">
            <div className="max-w-[1440px] mx-auto xl:px-8 relative">
                <Text
                    content={'Our <br class=\'md:hidden\' /> Bond Analyser Suite'}
                    variant="h2"
                    style={{
                        textShadow: '0px 0px 8px rgba(253, 209, 127, 0.50)',
                    }}
                    className="text-bond-analyser-homepage-herobanner-heading xl:text-center mb-12 xl:mb-8 h3-semibold !leading-10 xl:!leading-[96px] !text-[32px] xl:!text-[88px] w-max xl:mx-auto px-4 xl:px-0"
                    isInnerHtml
                />

                <TabHeader
                    tabs={tabsContent}
                    selectedTab={selectedTab}
                    onTabClick={handleTabClick}
                    wrapperClassName="xl:grid hidden"
                />

                <div className="w-full flex flex-col gap-12 xl:gap-16 mt-4">
                    {tabsContent.map((tab, idx) => (
                        <section
                            key={tab.id}
                            id={`tab-section-${tab.id}`}
                            ref={sectionRefs.current[idx]}
                            className="scroll-mt-32 cursor-pointer"
                            onClick={() => {
                                triggerCustomGaEvent(tab.gaEventName);
                                router.push(tab.link)
                            }}
                        >
                            <TabContent content={tab.content} label={tab.label} />
                        </section>
                    ))}
                </div>
            </div>
            {!auth && isClient ? (
                 <div className='w-full max-w-[1440px] mx-auto xl:px-8 relative'>
                    <div className="w-full flex justify-center mt-12 xl:mt-20 px-4 xl:px-0 items-center">
                        <div className="rounded-none bg-bond-analyser-advanced-tools-bg-mobile md:bg-bond-analyser-advanced-tools-bg bg-[length:100%_100%] bg-no-repeat xl:min-h-[560px] px-4 xl:px-0 py-12 w-full flex flex-col relative justify-center items-center">
                            <h2 className="h3-semibold xl:h1-semibold text-center mb-6 xl:mb-4 text-bond-analyser-home-page-categories-end-heading-gradient w-[224px] md:w-auto">
                                Advanced tools for{' '}
                                <span className="font-bold">
                                    deeper bond insights
                                </span>
                            </h2>
                            <p className="p4-regular xl:p1-regular text-white/80 text-center mb-8 max-w-2xl w-[296px] md:w-auto">
                                Login to unlock a powerful suite of tools that will
                                deepen your insights into the bond market and
                                elevate your investment game
                            </p>
                            <button 
                                className="px-5 py-3 rounded-full p4-regular xl:p3-medium transition-all text-primary-500 flex items-center gap-2 relative bg-basicWhite border-2 border-secondary-300 bond-analyser-login-now-button"
                                onClick={() => {
                                    router.push(
                                        {
                                            pathname: router.pathname,
                                            query: { ...router.query, redirect: '/bond-analyser/categories#advanced-tools' },
                                        },
                                        undefined,
                                        { scroll: false }
                                    );
                                    loginModal();
                                }}
                            >
                                Login now <ArrowRightIcon className='w-4'/>
                            </button>
                        </div>
                    </div>
                    </div>
                ) : null}
            <div className="absolute bottom-[-28px] left-0 w-max 2xl:w-full overflow-hidden h-[40px]">
                <img
                    src="/images/homepage/wave-pattern.svg"
                    className="w-full h-auto object-cover"
                />
            </div>
        </div>
    );
};

export default CategoriesTabs;
